import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { erApiFeil, erNettfeil } from '../api/feil.ts';
import { useSendEndringen } from '../api/useHypoteseBehandling.ts';
import { useHentHypotesePaaNytt, useHypoteseConfig, useHypoteseMedNummer, useOppfriskConfig, type Hypothesis, type HypothesisUpdate } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import type { Tast } from '../innboks/Tastaturkart.tsx';
import { useKart } from '../skall/kart.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { HypotesePille } from '../status/Pille.tsx';
import { hypoteseStatusNoekkel } from '../status/status.ts';
import { Brikker, Flate, Fot, Tekstknapp, Varsel } from './deler.tsx';
import { Konfliktkort } from './Innsending.tsx';
import { nummerTekst } from './Kvittering.tsx';
import { LeddFelt, svarFor } from './LeddFelt.tsx';
import { fornavn, statusNavn, stor } from './lesning.ts';
import { laasgrunn, leddFylt, porter } from './porter.ts';
import { delsetning, kortDatoAv, langDatoAv } from './setning.ts';
import { forLange, fraHypotese, LEDD, leddFor, tilPatch, ulikeFelt, type Felt, type LeddNoekkel, type Skjema } from './skjema.ts';
import type { Konflikt } from './useUtkastLagring.ts';

/**
 * «Endre hypotesen» (H2.2, resten — PR 2) på /h/{nr}/endre. Formen er konseptet v0.4 (DD-71): W8b
 * over 900, M4d under. Tilstandene, tastene og ordlyden der konseptet tier, er
 * `hypotese-innsending.jsx` (SideEndre: hvile, forKort, forkaste, konflikt, laast).
 *
 * Samme ledd og felt som setningsbyggeren (LeddFelt, skjema.ts, setning.ts) — ikke et eget skjema.
 * Forskjellene fra W3: spørsmålet etter «Trenger mer» står festet øverst med «Fristen står», det
 * endrede leddet har blyant og ordet «Endret» foran svaret (leses uten farge), og «Hva endret du?»
 * (`changeNote`, ≤ 280, valgfri) står under setningen. «Les over» er ikke et steg; setningen er
 * lesningen.
 *
 * INGEN autolagring og intet server-utkast: hver `PATCH` i mottatt og vurderes er en revisjon, så
 * endringen lever i minnet til «Send endringen». Uten nett står teksten til fanen lukkes (og
 * nettleseren spør først). «Lukk» med noe endret spør «Forkaste endringen?» — ingenting forsvinner
 * stille (DD-65). Ingen `localStorage` (K8).
 *
 * «Send endringen» er låst med grunnen ved knappen til minst ett ledd er endret OG klientens porter
 * er grønne — de samme som «Les over» (porter.ts). Tjeneren kjører dem også (H1.4): 409
 * `hypothesis_incomplete` henter minimumet på nytt, og klienten viser sin egen grunn og flytter
 * fokus til feltet. 409 `draft_changed_elsewhere`: det den andre endret tas inn; der begge endret
 * samme ledd, står konfliktkortet til personen velger. 409 `wording_locked`: teksten står, så den
 * kan kopieres.
 *
 * Bare innsenderens, og bare i mottatt og vurderes: en annens gir «Fant ikke» (K2, som
 * «Hypotesen min»), og en hypotese i en annen status sendes til /h/{nr}, som viser det som gjelder
 * (kontrakten: 409 `wording_locked` — formen er låst, men hypotesen finnes og er hennes).
 */

const TASTER: Tast[] = [
  ['Tab / Shift+Tab', 'neste og forrige felt'],
  ['Enter', 'åpner leddet på en rad eller en brikke'],
  ['Ctrl+Enter', 'Send endringen'],
  ['← →', 'flytter valget i retning, kilde og oppdatering'],
];

const NOTE_MAKS = 280;

const FELTNAVN: Partial<Record<Felt, string>> = {
  title: 'Hva heter den?',
  change: 'Endringen',
  audience: 'For hvem',
  effect: 'Hva tror du skjer?',
  metricName: 'Måltallet',
  sourceText: 'Kilden',
  unit: 'Enheten',
  whatWeLose: 'Mister vi noe om vi venter?',
  background: 'Hvorfor tror du det?',
};

/** Leddene som har en brikke i setningen (setning.ts, delsetning); resten står som rader under 900. */
const MED_BRIKKE = new Set<LeddNoekkel>(['endring', 'effekt', 'maaltall', 'kilde', 'rett']);

const kanEndres = (h: Hypothesis) => h.status === 'mottatt' || h.status === 'vurderes';

export function EndreSide({ nr }: { nr: number }) {
  const smal = useSmal();
  const me = useMe();
  const hypotese = useHypoteseMedNummer(nr);
  const tilbake = { navn: nummerTekst(nr), til: `/h/${nr}` };
  const tittel = smal ? `Endre ${nummerTekst(nr)}` : 'Endre hypotesen';
  // Når skjemaet først står, blir det stående: en ny henting som sier at hypotesen er valgt, skal
  // ikke ta teksten fra personen. Tjeneren sier det selv (409 wording_locked) ved «Send endringen».
  const vist = useRef<Hypothesis | null>(null);

  if (vist.current) return <EndreSkjema h={vist.current} />;

  if (hypotese.isPending || me.isPending) {
    return (
      <Flate tittel={tittel} tilbake={tilbake} bredde="skjema">
        <div role="status" aria-busy="true" aria-label="Laster" className="panel-skjelett">
          <span className="panel-skjelett-linje panel-skjelett-tittel" />
          <span className="panel-skjelett-linje" />
        </div>
      </Flate>
    );
  }
  const h = hypotese.data;
  if (hypotese.isError || !h || h.submittedBy.id !== me.data?.id) {
    return (
      <Flate tittel={tittel} tilbake={{ navn: 'Mine hypoteser', til: '/h/mine' }}>
        {/* K2: en hypotese som ikke finnes, sier det samme som en som ikke er din. */}
        <Varsel>{hypotese.isError && erNettfeil(hypotese.error) ? 'Fikk ikke hentet hypotesen. Sjekk nettet og prøv igjen.' : 'Fant ikke hypotesen. Den kan være slettet, eller ikke synlig for deg.'}</Varsel>
      </Flate>
    );
  }
  if (!kanEndres(h)) return <Navigate to={`/h/${nr}`} replace />;
  vist.current = h;
  return <EndreSkjema h={h} />;
}

function EndreSkjema({ h }: { h: Hypothesis }) {
  const smal = useSmal();
  const naviger = useNavigate();
  const me = useMe();
  const config = useHypoteseConfig();
  const oppfriskConfig = useOppfriskConfig();
  const hentPaaNytt = useHentHypotesePaaNytt();
  const sender = useSendEndringen();
  const kart = useKart();
  useEffect(() => {
    kart.meldInn('Endringen', TASTER);
    return () => kart.rydd();
  }, [kart]);

  const nr = h.number!;
  const tilHypotesen = `/h/${nr}`;
  const [base, setBase] = useState<Skjema>(() => fraHypotese(h));
  const [versjon, setVersjon] = useState(h.version);
  const [skjema, setSkjema] = useState<Skjema>(base);
  const [note, setNote] = useState('');
  const [aapent, setAapent] = useState<LeddNoekkel | null>(null);
  const [fokus, setFokus] = useState<{ felt?: Felt; n: number } | null>(null);
  const [konflikt, setKonflikt] = useState<Konflikt | null>(null);
  const [tattInn, setTattInn] = useState(false);
  const [laast, setLaast] = useState<Hypothesis | null>(null);
  const [forkaster, setForkaster] = useState(false);
  const [fokusEtterFeil, setFokusEtterFeil] = useState(false);

  const sporsmaal = h.awaitingSubmitter ? [...h.responses].reverse().find((r) => r.kind === 'trenger_mer') : undefined;
  const endret = useMemo(() => ulikeFelt(skjema, base), [skjema, base]);
  const endredeLedd = useMemo(() => new Set(endret.map(leddFor)), [endret]);
  const min = config.data?.minFieldLength;
  const mangler = useMemo(() => (min === undefined ? [] : porter(skjema, min)), [skjema, min]);
  const skittent = endret.length > 0 || note.trim() !== '';

  const aapne = useCallback((ledd: LeddNoekkel) => {
    setAapent(ledd);
    setFokus((f) => ({ n: (f?.n ?? 0) + 1 }));
  }, []);

  // Fokus i leddet som åpnes: feltet som må rettes, ellers det første.
  useEffect(() => {
    if (!fokus) return;
    const rot = document.getElementById('he-aapent');
    if (!rot) return;
    const maal = fokus.felt ? rot.querySelector<HTMLElement>(`[data-testid="h-${fokus.felt}"]`) : null;
    const felt = maal && maal.getAttribute('role') === 'radiogroup' ? maal.querySelector<HTMLElement>('[role="radio"][tabindex="0"]') : maal;
    (felt ?? rot.querySelector<HTMLElement>('input, textarea, [role="radio"][tabindex="0"]'))?.focus();
  }, [fokus]);

  // 409 hypothesis_incomplete: minimumet er hentet på nytt — leddet med det første som mangler åpnes.
  useEffect(() => {
    if (!fokusEtterFeil) return;
    setFokusEtterFeil(false);
    const m = mangler[0];
    if (!m) return;
    setAapent(m.ledd);
    setFokus((f) => ({ felt: m.felt, n: (f?.n ?? 0) + 1 }));
  }, [fokusEtterFeil, mangler]);

  // Lukkes fanen med noe endret, spør nettleseren først: teksten finnes bare her.
  useEffect(() => {
    if (!skittent) return undefined;
    const foerLukk = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', foerLukk);
    return () => window.removeEventListener('beforeunload', foerLukk);
  }, [skittent]);

  const oppdater = useCallback(
    (patch: Partial<Skjema>) => {
      setSkjema((s) => ({ ...s, ...patch }));
      if (sender.error) sender.reset();
    },
    [sender],
  );

  const feil = sender.error;
  const tjenerNekter = erApiFeil(feil) && feil.code === 'hypothesis_incomplete' && mangler.length === 0;
  const lange = forLange(skjema);
  const noteOver = note.length - NOTE_MAKS;
  const grunn = laast
    ? 'Den kan ikke endres lenger.'
    : konflikt
      ? 'Velg versjon først.'
      : min === undefined
        ? 'Henter kravene til hypotesen …'
        : endret.length === 0
          ? 'Endre minst ett ledd først.'
          : lange[0]
            ? `Kort ned «${FELTNAVN[lange[0].felt] ?? LEDD.find((l) => l.k === leddFor(lange[0]!.felt))!.sp}» med ${lange[0].over} tegn først.`
            : mangler.length > 0
              ? laasgrunn(mangler, { harNavn: true })
              : noteOver > 0
                ? `Kort ned «Hva endret du?» med ${noteOver} tegn først.`
                : tjenerNekter
                  ? `${me.data?.tenantName ?? 'Leietakeren'} har endret kravene. Se over leddene og prøv igjen.`
                  : null;

  const konfliktFra = (server: Hypothesis) => {
    // Det den andre endret siden siden ble åpnet, og det jeg har endret. Det bare den andre endret,
    // tas inn i skjemaet — ellers ville «Send endringen» skrevet det gamle tilbake.
    const der = fraHypotese(server);
    const andre = ulikeFelt(base, der);
    const mine = new Set(endret);
    const ulikt = new Set(ulikeFelt(skjema, der));
    const inn: Partial<Skjema> = {};
    for (const f of andre) if (!mine.has(f)) (inn as Record<string, unknown>)[f] = der[f];
    setSkjema((s) => ({ ...s, ...inn }));
    const begge = andre.filter((f) => mine.has(f) && ulikt.has(f));
    if (begge.length === 0) {
      setBase(der);
      setVersjon(server.version);
      setTattInn(true);
      return;
    }
    setKonflikt({ server, felt: begge });
    setAapent(leddFor(begge[0]!));
  };

  const brukLagret = () => {
    if (!konflikt) return;
    const der = fraHypotese(konflikt.server);
    const inn: Partial<Skjema> = {};
    for (const f of konflikt.felt) (inn as Record<string, unknown>)[f] = der[f];
    setSkjema((s) => ({ ...s, ...inn }));
    setBase(der);
    setVersjon(konflikt.server.version);
    setKonflikt(null);
  };
  const beholdDenne = () => {
    if (!konflikt) return;
    setBase(fraHypotese(konflikt.server));
    setVersjon(konflikt.server.version);
    setKonflikt(null);
  };

  const sendNaa = () => {
    if (grunn || sender.isPending || forkaster) return;
    // Merge-patch: bare det som er endret. Et tømt tall, en tom dato eller et fjernet valg går som null.
    const alt = tilPatch(skjema) as Record<string, unknown>;
    const body: Record<string, unknown> = {};
    for (const f of endret) if (f in alt) body[f] = alt[f];
    body.baseVersion = versjon;
    if (note.trim()) body.changeNote = note.trim();
    setTattInn(false);
    sender.mutate(
      { id: h.id, body: body as unknown as HypothesisUpdate },
      {
        onSuccess: (ny) => {
          const tekst = ny.reviewer ? `Endringen er sendt. ${stor(fornavn(ny.reviewer))} får beskjed.` : 'Endringen er sendt.';
          void naviger(tilHypotesen, { state: { endret: tekst } });
        },
        onError: (e) => {
          if (!erApiFeil(e)) return;
          if (e.code === 'draft_changed_elsewhere') {
            void hentPaaNytt(h.id).then(konfliktFra, () => undefined);
            sender.reset();
          } else if (e.code === 'wording_locked') {
            // Teksten står, så den kan kopieres. Hvilken status den fikk, hentes for banneret.
            setLaast(h);
            void hentPaaNytt(h.id).then(setLaast, () => undefined);
          } else if (e.code === 'hypothesis_incomplete' || e.code === 'text_too_short' || e.code === 'threshold_not_numeric') {
            // Minimumet kan være hevet siden siden ble åpnet: klientens porter kjøres på nytt.
            void oppfriskConfig().then(() => setFokusEtterFeil(true));
          }
        },
      },
    );
  };

  const lukk = () => (skittent ? setForkaster(true) : void naviger(tilHypotesen));
  const onTilbake = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!skittent) return;
    e.preventDefault();
    setForkaster(true);
  };

  const annenFeil =
    feil && !(erApiFeil(feil) && ['hypothesis_incomplete', 'draft_changed_elsewhere', 'wording_locked'].includes(feil.code))
      ? erNettfeil(feil)
        ? 'Fikk ikke sendt: uten nett. Teksten står, prøv igjen når nettet er tilbake.'
        : erApiFeil(feil)
          ? feil.brukertekst
          : 'Fikk ikke sendt. Prøv igjen.'
      : null;

  const banner = laast ? (
    <Varsel>
      {laast.status === 'valgt'
        ? `${nummerTekst(nr)} ble valgt mens du skrev, og ordlyden kan ikke endres lenger. Teksten din står under, så du kan kopiere den. Eieren kan foreslå en justering før start.`
        : `${nummerTekst(nr)} kan ikke endres lenger${statusNavn(config.data, laast) && laast.status !== h.status ? ` — den er ${statusNavn(config.data, laast).toLowerCase()} nå` : ''}. Teksten din står under, så du kan kopiere den.`}
    </Varsel>
  ) : null;
  const meldinger = (
    <>
      {banner}
      {annenFeil ? <Varsel>{annenFeil}</Varsel> : null}
      {tattInn ? (
        <Varsel tone="info" rolle="status">
          Hypotesen er endret et annet sted siden du åpnet den. Det som ble endret der, står nå i skjemaet. Se over og send endringen igjen.
        </Varsel>
      ) : null}
    </>
  );

  const fot = (
    <Fot
      venstre={
        <Tekstknapp onClick={lukk} testid="h-lukk">
          Lukk
        </Tekstknapp>
      }
      grunn={grunn}
      primar={{ label: sender.isPending ? 'Sender …' : 'Send endringen', onClick: sendNaa, opptatt: sender.isPending, ikon: 'send-outline', testid: 'he-send' }}
    />
  );

  const konfliktLedd = konflikt ? leddFor(konflikt.felt[0]!) : null;
  const konfliktKort = konflikt ? <Konfliktkort konflikt={konflikt} onBrukLagret={brukLagret} onBehold={beholdDenne} hode="Hypotesen er endret et annet sted siden du åpnet den" /> : null;
  const kort = sporsmaal ? <SporsmaalFestet sporsmaal={sporsmaal} frist={h.deadlineAt ? langDatoAv(h.deadlineAt) : null} /> : null;
  // «Mister vi noe» og «Hvorfor» står bare når de var fylt ved innsending (W8b).
  const synlige = LEDD.filter((l) => !l.valgfri || svarFor(l.k, base) !== null);
  const aapentLedd = aapent ? LEDD.find((l) => l.k === aapent) : undefined;
  const aapentInnhold = aapentLedd ? (
    <>
      <h2 className="h-sporsmal" id={`sp-${aapentLedd.k}`}>
        {endredeLedd.has(aapentLedd.k) ? (
          <>
            <Ikon navn="create-outline" className="he-blyant" />
            <span className="panel-skjult">Endret: </span>
          </>
        ) : null}
        {aapentLedd.sp}
      </h2>
      <LeddFelt k={aapentLedd.k} s={skjema} oppdater={oppdater} spId={`sp-${aapentLedd.k}`} />
      {konfliktKort && konfliktLedd === aapentLedd.k ? konfliktKort : null}
    </>
  ) : null;
  const noteFelt = <NoteFelt verdi={note} onEndre={setNote} />;
  const dialog = forkaster ? (
    <Dialog tittelId="he-forkast-h" onLukk={() => setForkaster(false)} testid="he-forkast-dialog">
      <h2 id="he-forkast-h" className="panel-h2">
        Forkaste endringen?
      </h2>
      <p className="panel-tekst">{`Det du har endret i ${nummerTekst(nr)} er ikke sendt. Hypotesen står som den var${sporsmaal ? ', og spørsmålet venter fortsatt på deg' : ''}.`}</p>
      <div className="panel-handlinger">
        <Knapp label="Forkast endringen" primaer onClick={() => void naviger(tilHypotesen)} testid="he-forkast" />
        <Knapp label="Avbryt" kbd="Esc" fokusFørst onClick={() => setForkaster(false)} />
      </div>
    </Dialog>
  ) : null;

  if (smal) {
    // M4d: én kolonne. Spørsmålet øverst, setningen der hver brikke åpner sitt ledd, de endrede
    // leddene med blyant og «Endret», og «Hva endret du?» over foten.
    // Det endrede leddet står med ramme og blyant i setningen (M4d), som leddet som skrives nå.
    const deler = delsetning(skjema, { aapent: aapent ?? undefined }).map((d) => (d.ledd && endredeLedd.has(d.ledd) ? { ...d, type: 'on' as const, endret: true } : d));
    return (
      <Flate tittel={`Endre ${nummerTekst(nr)}`} tilbake={{ navn: nummerTekst(nr), til: tilHypotesen }} fot={fot} bredde="skjema" onTilbake={onTilbake}>
        <div className="he-endre" data-testid="he-endre">
          {meldinger}
          {kort}
          {/* M4d: setningen i h3 rett på flaten — ikke i boksen fra M2, der den er framdriften. */}
          <Brikker deler={deler} str="h3" onAapne={aapne} />
          {/* Leddene uten egen brikke i setningen (tittelen, tallet i dag, de valgfrie som var fylt):
              samme rader som over 900, så hvert ledd kan endres også på telefonen. */}
          <ol aria-label="Andre ledd" className="h-ledd">
            {synlige
              .filter((l) => !MED_BRIKKE.has(l.k) && l.k !== aapent)
              .map((l) => (
                <EndreRad key={l.k} k={l.k} sp={l.sp} svar={svarFor(l.k, skjema)} endret={endredeLedd.has(l.k)} fylt={min === undefined || leddFylt(mangler, l.k)} onAapne={() => aapne(l.k)} />
              ))}
          </ol>
          {aapentInnhold ? (
            <div id="he-aapent" className="h-ledd-aapent" data-testid={`he-ledd-${aapent}`}>
              {aapentInnhold}
            </div>
          ) : null}
          {konfliktKort && !aapentInnhold ? konfliktKort : null}
          {endredeLedd.size > 0 ? (
            <ul className="he-endret-rader" aria-label="Det du har endret" data-testid="he-endret">
              {LEDD.filter((l) => endredeLedd.has(l.k)).map((l) => (
                <li key={l.k} className="he-endret-rad">
                  <Ikon navn="create-outline" className="he-blyant" />
                  <span>
                    <b className="he-endret-ord">Endret</b>
                    {` · ${svarFor(l.k, skjema) ?? 'tomt'}`}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          {noteFelt}
        </div>
        {dialog}
      </Flate>
    );
  }

  const deler = delsetning(skjema, { aapent: aapent ?? undefined });
  return (
    <Flate tittel="Endre hypotesen" tilbake={{ navn: nummerTekst(nr), til: tilHypotesen }} fot={fot} bredde="skjema" onTilbake={onTilbake}>
      <div className="he-endre" data-testid="he-endre">
        {meldinger}
        <div className="h-skjema">
          <div className="he-venstre">
            {kort}
            <ol aria-label="Leddene" className="h-ledd">
              {synlige.map((l) =>
                l.k === aapent ? (
                  <li key={l.k} id="he-aapent" aria-current="step" className="h-ledd-aapent" data-testid={`he-ledd-${l.k}`}>
                    {aapentInnhold}
                  </li>
                ) : (
                  <EndreRad key={l.k} k={l.k} sp={l.sp} svar={svarFor(l.k, skjema)} endret={endredeLedd.has(l.k)} fylt={min === undefined || leddFylt(mangler, l.k)} onAapne={() => aapne(l.k)} />
                ),
              )}
            </ol>
          </div>
          <div className="h-setning-kol">
            {konfliktKort && konfliktLedd !== aapent ? konfliktKort : null}
            <div className="h-setning-hode">
              <HypotesePille noekkel={hypoteseStatusNoekkel(h.status, h.outcome)} navn={statusNavn(config.data, h) || h.status} />
              <p className={skjema.title.trim() ? 'h-setning-tittel' : 'h-setning-tittel h-setning-tittel-tom'}>{skjema.title.trim() || 'Uten tittel'}</p>
            </div>
            <Brikker deler={deler} str="h3" onAapne={aapne} />
            {noteFelt}
          </div>
        </div>
      </div>
      {dialog}
    </Flate>
  );
}

/**
 * Spørsmålet etter «Trenger mer», festet øverst (W8b, M4d): hvem som spør, når, spørsmålet, og at
 * fristen står — endringen flytter ingen frist. Uten knapp: svaret er skjemaet under.
 */
function SporsmaalFestet({ sporsmaal, frist }: { sporsmaal: Hypothesis['responses'][number]; frist: string | null }) {
  return (
    <section className="hm-kort he-sporsmaal" aria-labelledby="he-sporsmaal-h" data-testid="he-sporsmaal">
      <div className="hm-kort-hode">
        <b id="he-sporsmaal-h" className="hm-kort-tittel">
          <Ikon navn="help-circle-outline" className="hm-kort-ikon" />
          {`${stor(fornavn(sporsmaal.by))} lurer på én ting`}
        </b>
        <span className="hb-liten">{kortDatoAv(sporsmaal.at)}</span>
      </div>
      <p className="hb-tekst">{sporsmaal.text}</p>
      {frist ? <p className="hb-dempet">{`Fristen står: ${frist}.`}</p> : null}
    </section>
  );
}

/**
 * En lukket rad: ✓ når leddet holder porten, blyant i merkefargen og ordet «Endret» foran svaret
 * når det avviker fra gjeldende form — leses uten farge (W8b). Stiplet sirkel når noe mangler.
 */
function EndreRad({ k, sp, svar, endret, fylt, onAapne }: { k: LeddNoekkel; sp: string; svar: string | null; endret: boolean; fylt: boolean; onAapne: () => void }) {
  return (
    <li>
      <button type="button" className="h-ledd-rad" onClick={onAapne} aria-label={`${sp}${endret ? ' Endret:' : ''} ${svar ?? 'Ikke fylt ut'}`} data-testid={`he-ledd-${k}`}>
        {endret ? <Ikon navn="create-outline" className="he-blyant he-ledd-ikon" /> : fylt ? <Ikon navn="checkmark-outline" className="h-ledd-fylt" /> : <span aria-hidden="true" className="h-ledd-tom" />}
        <span className="h-ledd-tekst">
          <span className="h-ledd-sp">{sp}</span>
          <span className="h-ledd-svar">
            {endret ? (
              <>
                <b className="he-endret-ord">Endret</b>
                {' · '}
              </>
            ) : null}
            {svar ?? 'Ikke fylt ut'}
          </span>
        </span>
      </button>
    </li>
  );
}

/** «Hva endret du? · valgfritt» (`changeNote`, ≤ 280). Telleren fra 80 %, i warning over grensen (DD-44). */
function NoteFelt({ verdi, onEndre }: { verdi: string; onEndre: (v: string) => void }) {
  const vis = verdi.length >= NOTE_MAKS * 0.8;
  const over = verdi.length > NOTE_MAKS;
  return (
    <div className="h-felt he-note">
      <span className="h-felt-hode">
        <label htmlFor="he-note" className="h-etikett">
          Hva endret du? · valgfritt
        </label>
        {vis ? (
          <span className={over ? 'h-teller h-teller-over' : 'h-teller'}>
            {over ? <Ikon navn="warning-outline" className="h-teller-ikon" /> : null}
            {verdi.length} / {NOTE_MAKS}
          </span>
        ) : null}
      </span>
      <textarea id="he-note" className="h-input h-tekstfelt" rows={3} value={verdi} aria-invalid={over || undefined} onChange={(e) => onEndre(e.target.value)} data-testid="he-note" />
    </div>
  );
}
